import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Link,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StrategyPerformance from '../components/strategies/StrategyPerformance';

const StrategyPage = () => {
  const [strategies, setStrategies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState({
    name: '',
    description: '',
    entryCriteria: '',
    exitCriteria: '',
    tradingviewUrl: '',
    createdAt: new Date().toISOString().slice(0, 10),
    tags: ''
  });

  const fetchStrategies = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get(
        'https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/createTradeStrategy',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        }
      );
      setStrategies(response.data);
    } catch (error) {
      console.error("Error fetching strategies: ", error);
    }
  };

  useEffect(() => {
    fetchStrategies();
  }, []);

  const handleOpen = () => {
    setEditingStrategy(null);
    setForm({
      name: '',
      description: '',
      entryCriteria: '',
      exitCriteria: '',
      tradingviewUrl: '',
      createdAt: new Date().toISOString().slice(0, 10),
      tags: ''
    });
    setOpen(true);
  };

  const handleEdit = (strategy) => {
    setEditingStrategy(strategy);
    setForm(strategy);
    setOpen(true);
  };

  const handleDelete = async (strategyId) => {
    try {
      const token = localStorage.getItem('idToken');
      await axios.delete('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/createTradeStrategy', {
        data: { strategyId },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      fetchStrategies();
    } catch (error) {
      console.error('Error deleting strategy:', error.response?.data || error.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('idToken');
      await axios.post(
        'https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/createTradeStrategy',
        form,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        }
      );
      setOpen(false);
      fetchStrategies();
    } catch (error) {
      console.error('Error saving strategy:', error);
    }
  };

  const columns = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
    { field: 'description', headerName: 'Description', flex: 2, minWidth: 200 },
    {
      field: 'tradingviewUrl',
      headerName: 'View URL',
      flex: 1,
      minWidth: 100,
      renderCell: (params) =>
        params.value ? (
          <Link href={params.value} target="_blank" rel="noopener">
            <VisibilityIcon />
          </Link>
        ) : null
    },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 1,
      minWidth: 150
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)}>
            <EditIcon />
          </IconButton>
          <IconButton color="error" onClick={() => handleDelete(params.row.strategyId)}>
            <DeleteIcon />
          </IconButton>
        </>
      )
    }
  ];

  // Mobile view for strategies
  const renderMobileStrategies = () => (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {strategies.map((strategy) => (
          <Grid item xs={12} key={strategy.strategyId}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                  {strategy.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {strategy.description}
                </Typography>
                
                {strategy.tags && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Tags:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {strategy.tags.split(',').map((tag, idx) => (
                        <Chip 
                          key={idx} 
                          label={tag.trim()} 
                          size="small" 
                          variant="outlined" 
                          color="primary"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                
                <Divider sx={{ my: 1.5 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  {strategy.tradingviewUrl && (
                    <Button 
                      startIcon={<VisibilityIcon />} 
                      size="small" 
                      component={Link} 
                      href={strategy.tradingviewUrl} 
                      target="_blank"
                      sx={{ mr: 1 }}
                    >
                      View
                    </Button>
                  )}
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                    <Button 
                      startIcon={<EditIcon />} 
                      size="small" 
                      variant="outlined"
                      onClick={() => handleEdit(strategy)}
                    >
                      Edit
                    </Button>
                    <Button 
                      startIcon={<DeleteIcon />} 
                      size="small" 
                      variant="outlined" 
                      color="error"
                      onClick={() => handleDelete(strategy.strategyId)}
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
        Strategy Management
      </Typography>
      
      <Tabs 
        value={tabValue} 
        onChange={(e, newValue) => setTabValue(newValue)} 
        sx={{ mb: 3 }}
        variant={isMobile ? "fullWidth" : "standard"}
      >
        <Tab label="Strategies" />
        <Tab label="Performance" />
      </Tabs>

      {tabValue === 0 && (
        <>
          <Button 
            variant="contained" 
            onClick={handleOpen} 
            sx={{ mb: 2, width: { xs: '100%', sm: 'auto' } }}
            fullWidth={isMobile}
          >
            Create Strategy
          </Button>

          {isMobile ? (
            renderMobileStrategies()
          ) : (
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <DataGrid
                rows={strategies}
                columns={columns}
                autoHeight
                getRowId={(row) => row.strategyId}
                sx={{ minWidth: 800 }}
              />
            </Box>
          )}
        </>
      )}

      {tabValue === 1 && <StrategyPerformance />}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            boxShadow: 24
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600 }}>
          {editingStrategy ? 'Edit Strategy' : 'Create New Strategy'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
            <TextField
              label="Strategy Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Description"
              name="description"
              value={form.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Entry Criteria"
              name="entryCriteria"
              value={form.entryCriteria}
              onChange={handleChange}
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              placeholder="Define when to enter trades..."
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Exit Criteria"
              name="exitCriteria"
              value={form.exitCriteria}
              onChange={handleChange}
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              placeholder="Define when to exit trades..."
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="TradingView URL"
              name="tradingviewUrl"
              value={form.tradingviewUrl}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Tags (comma-separated)"
              name="tags"
              value={form.tags}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            {form.tags && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {form.tags.split(',').map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag.trim()}
                    size="small"
                    color="primary"
                  />
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: { xs: 1, sm: 2 }, flexDirection: isMobile ? 'column' : 'row' }}>
          <Button 
            onClick={() => setOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3, width: isMobile ? '100%' : 'auto' }}
            fullWidth={isMobile}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{ borderRadius: 2, px: 3, width: isMobile ? '100%' : 'auto' }}
            fullWidth={isMobile}
          >
            {editingStrategy ? 'Update Strategy' : 'Create Strategy'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StrategyPage;